/**
 * Microphone selection for the web voice lanes.
 *
 * Moved verbatim from the retired ElevenLabs dictation client (the Line,
 * spec 2026-09-11, section 6): the mic decision and its cache are the one
 * piece of that lane the live session still needs. The two hazards below are
 * real and were found the hard way; keep them.
 */

// ── microphone selection ─────────────────────────────────────────────────────
// THE SYSTEM DEFAULT IS THE PERSON'S OWN CHOICE, AND WE FOLLOW IT. Every other
// calling app does, and the input someone picked in their sound settings is the
// input they expect to be heard on. Exactly one thing overrides it:
//   1. macOS Continuity hands getUserMedia the *iPhone* mic when that's the
//      system default, lighting the phone's mic indicator. Never request it.
//
// There USED to be a second override, and it cost us the product (staging,
// 2026-09-21). Opening a Bluetooth earbud's mic drops the headset from A2DP to
// HFP: output mutes for about a second and the capture is narrowband, so this
// file preferred the built-in mic whenever the default was earbuds. But the
// founder wears AirPods with the laptop across the room: six calls, 27 seconds
// of speech, and the vendor understood none of it, because we had quietly
// opened a microphone on the far side of the room and given him no way to say
// otherwise. The HFP switch costs the voice some quality; a microphone that
// cannot hear the person costs the whole call. Being heard wins.

export interface AudioInputInfo {
  kind: string;
  deviceId: string;
  label: string;
  /** The physical device an alias row ("default") shares with its real row. */
  groupId?: string;
}

// Exported for the silent-mic watchdog (mic-watch.ts): one set of label rules.
export const isContinuityPhone = (label: string): boolean =>
  /iphone|ipad|continuity/i.test(label);
const isBluetoothEarbud = (label: string): boolean =>
  /airpod|earbud|buds|headset|bluetooth/i.test(label);
export const isBuiltIn = (label: string): boolean =>
  /built-in|macbook|internal|imac|mac ?mini|studio/i.test(label);

/**
 * The evidence line's device (2026-09-21): the CLASS of an input, by the rules
 * above. PRIVACY: a label names a person ("Keshav's AirPods Pro"), so the
 * label never leaves the browser; one of these five words does.
 */
export type MicDeviceClass =
  "builtin" | "bluetooth" | "phone" | "other" | "unknown";
export function micDeviceClass(label: string): MicDeviceClass {
  if (!label) return "unknown";
  if (isContinuityPhone(label)) return "phone";
  if (isBluetoothEarbud(label)) return "bluetooth";
  if (isBuiltIn(label)) return "builtin";
  return "other";
}

/** "Default - MacBook Pro Microphone" and "MacBook Pro Microphone" are one device. */
const bareLabel = (label: string): string =>
  label.replace(/^.*? - /, "").trim();

/**
 * WHAT WORKED LAST TIME (2026-09-21, the deaf calls). The chooser follows the system default
 * now, which is what fixed the founder's calls — so this is no longer the escape hatch from our
 * own override, and it is a narrower thing than it was written to be: it is for the person whose
 * CHOSEN input is the one that cannot carry them. They have no way to know that, and nothing in
 * their sound settings would tell them. When a deaf call is HEARD after the heal swaps the
 * input, that input's CLASS is remembered here and the next call starts on it, above the default.
 *
 * PRIVACY: a device CLASS, never a label or an id. "bluetooth" names nobody; "Keshav's AirPods
 * Pro" names him. Per machine, and never sent to the server.
 */
export const MIC_PREF_KEY = "line.mic.pref";
const MIC_CLASSES: MicDeviceClass[] = [
  "builtin",
  "bluetooth",
  "phone",
  "other",
  "unknown",
];

/** The remembered class, or null. Storage can be off, full or blocked: none of that is an error here. */
export function readMicPref(): MicDeviceClass | null {
  try {
    const raw = window.localStorage.getItem(MIC_PREF_KEY);
    return MIC_CLASSES.includes(raw as MicDeviceClass)
      ? (raw as MicDeviceClass)
      : null;
  } catch {
    return null;
  }
}

export function writeMicPref(rank: MicDeviceClass): void {
  try {
    window.localStorage.setItem(MIC_PREF_KEY, rank);
  } catch {
    /* a preference nobody can store is a preference nobody misses */
  }
}

export function clearMicPref(): void {
  try {
    window.localStorage.removeItem(MIC_PREF_KEY);
  } catch {
    /* same */
  }
}

/**
 * Pick the input deviceId to request, or `undefined` to use the system default.
 * Pure (takes a device list) so it can be unit-tested without a real mediaDevices.
 *
 * `undefined` is the ANSWER, not a fallback: the system default is what the person chose, and
 * the only reason to name a device instead is the Continuity phone, which macOS can hand us
 * behind the default's back.
 *
 * `prefer` is the one thing that outranks even the default: the class a deaf call was HEARD on
 * after the heal swapped inputs on this machine. It is not a second guess at what they want, it
 * is the only part of this function that was ever measured rather than reasoned about, and it
 * exists because a default that cannot carry them is exactly the case they cannot fix themselves.
 */
export function choosePreferredMic(
  devices: AudioInputInfo[],
  prefer?: MicDeviceClass | null
): string | undefined {
  const inputs = devices.filter((d) => d.kind === "audioinput");
  if (!inputs.some((d) => d.label)) return undefined; // labels need permission → use default

  const real = inputs.filter(
    (d) =>
      d.deviceId && d.deviceId !== "default" && d.deviceId !== "communications"
  );

  // What was HEARD here before, above everything: a default that cannot carry
  // them is the one case they have no way to fix from their sound settings.
  if (prefer) {
    const known = real.find(
      (d) => !isContinuityPhone(d.label) && micDeviceClass(d.label) === prefer
    );
    if (known) return known.deviceId;
  }

  // No Continuity phone anywhere: nothing else may override what they chose,
  // earbuds included.
  if (!inputs.some((d) => isContinuityPhone(d.label))) return undefined;

  // A Continuity phone IS present, and macOS can hand it to getUserMedia even when
  // the 'default' row doesn't name it — so the default cannot be requested blind.
  // Same principle, one step more work: when the default names a real non-phone
  // device, ask for THAT device's own row by id.
  const realNonPhone = real.filter((d) => !isContinuityPhone(d.label));
  if (realNonPhone.length === 0) return undefined;

  const defaultEntry = inputs.find((d) => d.deviceId === "default");
  if (defaultEntry && !isContinuityPhone(defaultEntry.label)) {
    const behind = realNonPhone.find(
      (d) =>
        (!!defaultEntry.groupId && d.groupId === defaultEntry.groupId) ||
        (!!d.label && bareLabel(d.label) === bareLabel(defaultEntry.label))
    );
    if (behind) return behind.deviceId;
  }

  // The default IS the phone, or nothing in the list answers to it. Rank what is
  // left: they put the earbuds in their ears, so those come first; the built-in
  // next; anything else last. Never the phone.
  const score = (label: string): number => {
    if (isBluetoothEarbud(label) || /headphone|earphone/i.test(label)) return 3;
    if (isBuiltIn(label)) return 2;
    return 1;
  };
  return [...realNonPhone].sort((a, b) => score(b.label) - score(a.label))[0]
    ?.deviceId;
}

// The mic decision is CACHED off the per-hold hot path: every ms spent before
// getUserMedia is speech the user has already started saying and the mic never
// hears (the start cue plays at keydown). enumerateDevices re-runs only at PTT
// mount, on devicechange, and in the background after a grant (first-ever grant
// is when labels appear) — never inside a hold.
let micChoice: { id: string | undefined } | null = null;
let micChoiceInflight: Promise<string | undefined> | null = null;
let micChangeListenerArmed = false;
// The permission prompt is the slowest thing on the click path, and the
// browser asks it once per origin: the ball's first hover or focus can pay it.
let micPrimed = false;

async function resolveMicChoice(): Promise<string | undefined> {
  if (micChoiceInflight) return micChoiceInflight;
  micChoiceInflight = (async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const id = choosePreferredMic(
        devices as unknown as AudioInputInfo[],
        readMicPref()
      );
      micChoice = { id };
      return id;
    } catch {
      micChoice = { id: undefined };
      return undefined;
    } finally {
      micChoiceInflight = null;
    }
  })();
  return micChoiceInflight;
}

/** Warm the mic decision (idempotent). Call at PTT mount so holds never await it. */
export function primeMicChoice(): void {
  if (typeof navigator === "undefined" || !navigator.mediaDevices) return;
  if (
    !micChangeListenerArmed &&
    typeof navigator.mediaDevices.addEventListener === "function"
  ) {
    micChangeListenerArmed = true;
    navigator.mediaDevices.addEventListener("devicechange", () => {
      micChoice = null;
      void resolveMicChoice();
    });
  }
  if (!micChoice) void resolveMicChoice();
}

/** Test-only: cold-start the cache between tests. */
export function __resetMicChoiceForTests(): void {
  micChoice = null;
  micChoiceInflight = null;
  micPrimed = false;
  gumCalls = 0;
}

async function pickPreferredMicId(): Promise<string | undefined> {
  if (micChoice) return micChoice.id;
  return resolveMicChoice();
}

/**
 * getUserMedia calls this page load, for the evidence line. A page that asked once and a page
 * that asked six times are different stories about the same deaf call: the second has been
 * fighting the browser for a track, and every grab can hand back a different capture shape.
 */
let gumCalls = 0;
export const micGumCalls = (): number => gumCalls;

/** The capture shape of every line mic: the first one and a swapped one alike. */
const MIC_BASE: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
};

/**
 * getUserMedia for ONE named input: the silent-mic watchdog's swap
 * (mic-watch.ts). No fallback to the default (that is the input that went
 * dead; a refusal is the caller's to hear, so it tries the next) and no touch
 * of the chooser's cache: the next call decides afresh.
 */
export function getMicStreamById(deviceId: string): Promise<MediaStream> {
  gumCalls += 1;
  return navigator.mediaDevices.getUserMedia({
    audio: { ...MIC_BASE, deviceId: { exact: deviceId } },
  });
}

/** getUserMedia for the chosen mic; forces the device (exact) but degrades to the default if it vanishes. */
export async function getMicStream(): Promise<MediaStream> {
  gumCalls += 1;
  const base = MIC_BASE;
  const preferredId = await pickPreferredMicId();
  let stream: MediaStream;
  if (preferredId) {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { ...base, deviceId: { exact: preferredId } },
      });
    } catch {
      // chosen device gone between enumerate + capture — fall through to the default.
      stream = await navigator.mediaDevices.getUserMedia({ audio: base });
    }
  } else {
    stream = await navigator.mediaDevices.getUserMedia({ audio: base });
  }
  // Background refresh AFTER the grant: the first-ever grant is the moment device
  // labels become readable, so a pre-permission cache (no labels → default) heals
  // for the next hold without ever costing this one.
  micChoice = null;
  void resolveMicChoice();
  return stream;
}

/**
 * Settle the microphone permission off the click path: request the mic once
 * (the Dock calls this on the ball's first hover or focus) and stop its tracks
 * the moment they arrive. Never twice, never a live track while the line is
 * off, and never a word about a refusal: the click surfaces that itself.
 */
export function primeMic(): void {
  if (micPrimed) return;
  if (typeof navigator === "undefined" || !navigator.mediaDevices) return;
  micPrimed = true;
  void getMicStream()
    .then((stream) => {
      for (const t of stream.getTracks()) t.stop();
    })
    .catch(() => undefined);
}
